import {
    ApplicationConfig,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
    LOCALE_ID, provideAppInitializer, inject
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_CONFIG } from '../env/env.token';
import { env } from '../env/env';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideTradingProcedureService } from './infrastructure/trading-procedure/providers/provide-trading-procedure-service';
import {
    provideOrganizationListProviders
} from './infrastructure/organizations-list/providers/provide-organization-list';
import { provideOrganizationCreateProviders } from './infrastructure/organization-create/providers/provider';
import { TRADGINE_DATA_API_BASE_URL } from '@tradgine/data-access/tradgine-data';
import { AppConfig } from '../env/app-config.model';
import { authInterceptor } from './auth/interceptors/auth.interceptor';
import { HISTORY_API_BASE_URL } from '@tradgine/data-access/history';
import { AUTH_BASE_URL, AuthenticateApi } from '@tradgine/data-access/auth';
import { FILE_DOWNLOADER } from '../core/services/file-downloader/file-downloader.token';
import { FileDownloaderService } from '../core/services/file-downloader/file-downloader.service';
import { provideUserService } from './infrastructure/user-list/providers/provide-user-service';
import { AUCTION_FLOW_API_BASE_URL } from '@tradgine/data-access/auction-flow';
import { ALERT_SERVICE } from '../shared/alert/alert-service.token';
import { AlertService } from '../shared/alert/alert.service';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import { provideTradingProcedureListProviders } from './infrastructure/auction/providers';
import { NOTIFICATIONS_API_BASE_URL } from '@tradgine/data-access/notification';
import { httpErrorInterceptor } from '../core/interceptors/http-error.interceptor';
import { TimeService } from '@tradgine/time/core';
import { createHttpServerTime$ } from '@tradgine/time/adapters';
import { authErrorInterceptor } from './auth/interceptors/auth-error.interceptor';
import { filter, map } from 'rxjs/operators';
import { AUCTION_SEALED_FLOW_API_BASE_URL } from '@tradgine/data-access/auction-sealed-flow';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            const time = inject(TimeService);
            const authApi = inject(AuthenticateApi);
            const getServerTime$ = () =>
                authApi.getServerTime().pipe(
                    filter((dto) => dto.isOk()),
                    map((result) => new Date(result.unwrap().utc))
                );
            time.connect(createHttpServerTime$(getServerTime$, 60_000));
        }),
        provideHttpClient(
            withInterceptors([
                authInterceptor,
                httpErrorInterceptor,
                authErrorInterceptor,
            ])
        ),
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(appRoutes),
        {
            provide: LOCALE_ID,
            useFactory: () => {
                if (document?.documentElement?.lang === 'en') return 'en-GB';
                return document?.documentElement?.lang || 'en-GB';
            },
        },
        provideMomentDateAdapter(),
        {
            provide: MAT_DATE_FORMATS,
            useFactory: buildDateFormats,
            deps: [LOCALE_ID],
        },
        {
            provide: APP_CONFIG,
            useValue: env,
        },
        {
            provide: AUTH_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.authUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: TRADGINE_DATA_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.tradgineDataUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: AUCTION_FLOW_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.auctionFlowUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: AUCTION_SEALED_FLOW_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.auctionSealedFlowUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: HISTORY_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.historyUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: NOTIFICATIONS_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.notificationsUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: FILE_DOWNLOADER,
            useExisting: FileDownloaderService,
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' },
        },
        {
            provide: ALERT_SERVICE,
            useClass: AlertService,
        },
        provideTradingProcedureService(),
        provideTradingProcedureListProviders(),
        provideOrganizationListProviders(),
        provideOrganizationCreateProviders(),
        provideUserService(),
    ],
};

export function buildDateFormats(locale: string) {
    moment.locale(locale.toLowerCase());

    return {
        parse: {
            dateInput: 'L',
            timeInput: 'HH:mm',
        },
        display: {
            dateInput: 'L',
            monthYearLabel: 'MMM YYYY',
            dateA11yLabel: 'LL',
            monthYearA11yLabel: 'MMMM YYYY',
            timeInput: 'HH:mm',
            timeOptionLabel: 'HH:mm',
        },
    };
}
